import { supabase } from '../lib/supabase';

function toCamelCase(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
}

export const adminService = {
  // Get all users (renters + owners) with full profile data
  async getAllUsers(filters = {}) {
    try {
      let query = supabase?.from('user_profiles')?.select('*')?.order('created_at', { ascending: false });

      if (filters?.role && filters?.role !== 'all') {
        query = query?.eq('role', filters?.role);
      }
      if (filters?.status && filters?.status !== 'all') {
        query = query?.eq('account_status', filters?.status);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data?.map(toCamelCase) || [];
    } catch (error) {
      console.error('adminService.getAllUsers error:', error);
      return [];
    }
  },

  // Get full user details including bookings count, listings count
  async getUserDetails(userId) {
    try {
      const [profileResult, bookingsRenterResult, bookingsOwnerResult, listingsResult] = await Promise.all([
        supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single(),
        supabase?.from('bookings')?.select('id', { count: 'exact', head: true })?.eq('renter_id', userId),
        supabase?.from('bookings')?.select('id', { count: 'exact', head: true })?.eq('owner_id', userId),
        supabase?.from('trailer_listings')?.select('id', { count: 'exact', head: true })?.eq('owner_id', userId)
      ]);

      const profile = toCamelCase(profileResult?.data);
      return {
        ...profile,
        rentalsCount: bookingsRenterResult?.count || 0,
        bookingsAsOwnerCount: bookingsOwnerResult?.count || 0,
        listingsCount: listingsResult?.count || 0
      };
    } catch (error) {
      console.error('adminService.getUserDetails error:', error);
      return null;
    }
  },

  // Suspend a user
  async suspendUser(userId) {
    try {
      const { error } = await supabase?.from('user_profiles')?.update({ account_status: 'suspended', updated_at: new Date()?.toISOString() })?.eq('id', userId);
      if (error) throw error;
      return true;
    } catch (error) {
      console.error('adminService.suspendUser error:', error);
      throw error;
    }
  },

  // Activate a user
  async activateUser(userId) {
    try {
      const { error } = await supabase?.from('user_profiles')?.update({ account_status: 'active', updated_at: new Date()?.toISOString() })?.eq('id', userId);
      if (error) throw error;
      return true;
    } catch (error) {
      console.error('adminService.activateUser error:', error);
      throw error;
    }
  },

  // Get all bookings for admin oversight
  async getAllBookings(filters = {}) {
    try {
      let query = supabase?.from('bookings')?.select(`
          *,
          renter:user_profiles!bookings_renter_id_fkey(id, full_name, email),
          owner:user_profiles!bookings_owner_id_fkey(id, full_name, email),
          trailer:trailer_listings!bookings_trailer_listing_id_fkey(id, title, trailer_type)
        `)?.order('created_at', { ascending: false });

      if (filters?.status && filters?.status !== 'all') {
        query = query?.eq('status', filters?.status);
      }

      const { data, error } = await query?.limit(100);
      if (error) throw error;
      return data?.map(toCamelCase) || [];
    } catch (error) {
      console.error('adminService.getAllBookings error:', error);
      return [];
    }
  },

  // Get all transactions (bookings with payment info)
  async getAllTransactions(filters = {}) {
    try {
      let query = supabase?.from('bookings')?.select(`
          id,
          booking_reference,
          total_amount,
          platform_fee,
          payment_status,
          status,
          created_at,
          refund_amount,
          refund_status,
          renter:user_profiles!bookings_renter_id_fkey(id, full_name, email),
          owner:user_profiles!bookings_owner_id_fkey(id, full_name, email),
          trailer:trailer_listings!bookings_trailer_listing_id_fkey(id, title)
        `)?.not('payment_status', 'is', null)?.order('created_at', { ascending: false });

      if (filters?.paymentStatus && filters?.paymentStatus !== 'all') {
        query = query?.eq('payment_status', filters?.paymentStatus);
      }

      const { data, error } = await query?.limit(100);
      if (error) throw error;
      return data?.map(toCamelCase) || [];
    } catch (error) {
      console.error('adminService.getAllTransactions error:', error);
      return [];
    }
  },

  // Get payout history
  async getPayoutHistory() {
    try {
      const { data, error } = await supabase?.from('payout_transactions')?.select(`
          *,
          owner:user_profiles!payout_transactions_user_id_fkey(id, full_name, email)
        `)?.order('created_at', { ascending: false })?.limit(100);

      if (error) {
        // Table may not exist yet, return empty
        console.error('adminService.getPayoutHistory error:', error);
        return [];
      }
      return data?.map(toCamelCase) || [];
    } catch (error) {
      console.error('adminService.getPayoutHistory error:', error);
      return [];
    }
  },

  // Get all listings for admin
  async getAllListings(filters = {}) {
    try {
      let query = supabase?.from('trailer_listings')?.select(`
          *,
          owner:user_profiles!trailer_listings_owner_id_fkey(id, full_name, email)
        `)?.order('created_at', { ascending: false });

      if (filters?.status && filters?.status !== 'all') {
        query = query?.eq('status', filters?.status);
      }

      const { data, error } = await query?.limit(100);
      if (error) throw error;
      return data?.map(toCamelCase) || [];
    } catch (error) {
      console.error('adminService.getAllListings error:', error);
      return [];
    }
  },

  // Update listing status
  async updateListingStatus(listingId, status) {
    try {
      const { error } = await supabase?.from('trailer_listings')?.update({ status, updated_at: new Date()?.toISOString() })?.eq('id', listingId);
      if (error) throw error;
      return true;
    } catch (error) {
      console.error('adminService.updateListingStatus error:', error);
      throw error;
    }
  },

  // Process refund for a booking
  async processRefund(bookingId, refundAmount, reason) {
    try {
      const { error } = await supabase?.from('bookings')?.update({
          refund_amount: refundAmount,
          refund_status: 'processed',
          refund_reason: reason,
          updated_at: new Date()?.toISOString()
        })?.eq('id', bookingId);
      if (error) throw error;
      return true;
    } catch (error) {
      console.error('adminService.processRefund error:', error);
      throw error;
    }
  },

  // Get platform financial summary
  async getFinancialSummary() {
    try {
      const { data: bookings, error } = await supabase?.from('bookings')?.select('total_amount, platform_fee, payment_status, status, created_at, refund_amount, refund_status')?.eq('payment_status', 'succeeded');

      if (error) throw error;

      const totalRevenue = bookings?.reduce((sum, b) => sum + parseFloat(b?.total_amount || 0), 0) || 0;
      const totalFees = bookings?.reduce((sum, b) => sum + parseFloat(b?.platform_fee || 0), 0) || 0;
      const totalRefunds = bookings
        ?.filter(b => b?.refund_status === 'processed')
        ?.reduce((sum, b) => sum + parseFloat(b?.refund_amount || 0), 0) || 0;

      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1)?.toISOString();
      const monthlyRevenue = bookings
        ?.filter(b => b?.created_at >= monthStart)
        ?.reduce((sum, b) => sum + parseFloat(b?.total_amount || 0), 0) || 0;

      return {
        totalRevenue: Math.round(totalRevenue * 100) / 100,
        totalFees: Math.round(totalFees * 100) / 100,
        totalRefunds: Math.round(totalRefunds * 100) / 100,
        monthlyRevenue: Math.round(monthlyRevenue * 100) / 100,
        transactionCount: bookings?.length || 0
      };
    } catch (error) {
      console.error('adminService.getFinancialSummary error:', error);
      return { totalRevenue: 0, totalFees: 0, totalRefunds: 0, monthlyRevenue: 0, transactionCount: 0 };
    }
  },

  // Get owner verification requests (pending approvals)
  async getVerificationRequests(status = 'pending') {
    try {
      let query = supabase?.from('user_profiles')?.select('*')?.eq('role', 'owner')?.order('created_at', { ascending: false });

      if (status !== 'all') {
        query = query?.eq('verification_status', status);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data?.map(toCamelCase) || [];
    } catch (error) {
      console.error('adminService.getVerificationRequests error:', error);
      return [];
    }
  },

  // Approve owner verification
  async approveVerification(userId) {
    try {
      const { error } = await supabase?.from('user_profiles')?.update({ verification_status: 'verified', is_verified: true, updated_at: new Date()?.toISOString() })?.eq('id', userId);
      if (error) throw error;
      return true;
    } catch (error) {
      console.error('adminService.approveVerification error:', error);
      throw error;
    }
  },

  // Reject owner verification
  async rejectVerification(userId, reason) {
    try {
      const { error } = await supabase?.from('user_profiles')?.update({ verification_status: 'rejected', updated_at: new Date()?.toISOString() })?.eq('id', userId);
      if (error) throw error;
      return true;
    } catch (error) {
      console.error('adminService.rejectVerification error:', error);
      throw error;
    }
  },

  // Get marketplace metrics from real data
  async getMarketplaceMetrics() {
    try {
      const [listingsResult, bookingsResult, usersResult, revenueResult] = await Promise.all([
        supabase?.from('trailer_listings')?.select('id, status, trailer_type', { count: 'exact' }),
        supabase?.from('bookings')?.select('id, status, total_amount', { count: 'exact' }),
        supabase?.from('user_profiles')?.select('id, role, created_at', { count: 'exact' }),
        supabase?.from('bookings')?.select('total_amount')?.eq('payment_status', 'succeeded')
      ]);

      const activeListings = listingsResult?.data?.filter(l => l?.status === 'active')?.length || 0;
      const totalBookings = bookingsResult?.count || 0;
      const totalUsers = usersResult?.count || 0;
      const activeUsers = usersResult?.data?.filter(u => {
        const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
        return new Date(u.created_at) >= thirtyDaysAgo;
      })?.length || 0;

      const monthlyRevenue = revenueResult?.data?.reduce((sum, b) => sum + parseFloat(b?.total_amount || 0), 0) || 0;
      const avgBookingValue = totalBookings > 0 ? Math.round(monthlyRevenue / totalBookings) : 0;

      // Category breakdown
      const categoryMap = {};
      listingsResult?.data?.forEach(l => {
        if (l?.trailer_type) {
          categoryMap[l.trailer_type] = (categoryMap?.[l?.trailer_type] || 0) + 1;
        }
      });
      const totalListings = listingsResult?.data?.length || 1;
      const categoryBreakdown = Object.entries(categoryMap)?.map(([category, count]) => ({
        category,
        count,
        percentage: Math.round((count / totalListings) * 100)
      }));

      return {
        activeListings,
        totalBookings,
        monthlyRevenue: Math.round(monthlyRevenue),
        activeUsers,
        totalUsers,
        averageBookingValue: avgBookingValue,
        conversionRate: totalUsers > 0 ? Math.round((totalBookings / totalUsers) * 100) : 0,
        categoryBreakdown
      };
    } catch (error) {
      console.error('adminService.getMarketplaceMetrics error:', error);
      return {
        activeListings: 0, totalBookings: 0, monthlyRevenue: 0,
        activeUsers: 0, totalUsers: 0, averageBookingValue: 0,
        conversionRate: 0, categoryBreakdown: []
      };
    }
  },

  // Send admin message to a user (creates a system notification message)
  async sendMessageToUser(targetUserId, messageContent, subject) {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      if (authError || !user) throw new Error('Not authenticated');

      // Insert as a direct admin message in messages table
      // Use a special booking_id = null for admin messages
      const { data, error } = await supabase?.from('messages')?.insert({
          sender_id: user?.id,
          receiver_id: targetUserId,
          content: messageContent,
          subject: subject || 'Message from Platform Admin',
          is_read: false,
          created_at: new Date()?.toISOString()
        })?.select()?.single();

      if (error) {
        // If booking_id is required, try with a workaround
        console.error('adminService.sendMessageToUser error:', error);
        throw new Error(error.message || 'Failed to send message');
      }
      return toCamelCase(data);
    } catch (error) {
      console.error('adminService.sendMessageToUser error:', error);
      throw error;
    }
  },

  // Get all messages for admin oversight
  async getAllMessages() {
    try {
      const { data, error } = await supabase?.from('messages')?.select(`
          *,
          sender:user_profiles!messages_sender_id_fkey(id, full_name, email, role),
          receiver:user_profiles!messages_receiver_id_fkey(id, full_name, email, role)
        `)?.order('created_at', { ascending: false })?.limit(200);

      if (error) throw error;
      return data?.map(toCamelCase) || [];
    } catch (error) {
      console.error('adminService.getAllMessages error:', error);
      return [];
    }
  }
};

export default adminService;
